/**
 * ARC-61 — investment write guard (pure; no Firestore, no React).
 *
 * THE DEFECT CLASS
 * Sixth instance of ARC-14, after ARC-58 (income), ARC-59 (risk) and ARC-60
 * (debts). `investments`, `investmentHistory` and `investmentCategories` are
 * top-level fields of `financial_data/{uid}`, so every save REPLACES them
 * wholesale from this client's snapshot. Three load branches seeded them from
 * a demo scaffold, and two of those branches armed autosave first:
 *
 *   1. the failure branch        — setInvestments(def.investments)      loadedOk FALSE
 *   2. the empty-payload branch  — setInvestments(def.investments)      loadedOk TRUE
 *   3. the loaded-document branch — d.investments ?? getDefaultData().investments
 *                                                                      loadedOk TRUE
 *
 * (2) and (3) put nine invented holdings totalling $325,000 into the same state
 * `writeUserData` serialises, and autosave fired ~2s later with no member action
 * beyond opening the app. `investmentHistory` came along on the same branches
 * with a full year of invented monthly totals.
 *
 * Investments have a second injection path the other tabs do not: the tab's
 * `syncCurrentTotal` effect writes the current portfolio total into
 * `investmentHistory` whenever `totalInvestments > 0`. Seed the scaffold and
 * that effect stamps the scaffold's total into the history as well — a demo
 * figure that looks exactly like something the member entered.
 *
 * The rules here follow from that, and mirror ARC-60:
 *
 *   1. A read we do not trust produces no investment state and no write
 *      permission — `resolveInvestmentLoad`. A read that FAILED and a read whose
 *      shape we cannot PARSE collapse to one identical closed state, because
 *      both leave us equally ignorant of what the server holds.
 *   2. There is no branch anywhere in this module that returns a placeholder
 *      holding or a placeholder month. Investments are either the server's array
 *      or empty — the scaffold is gone from `getDefaultData()` entirely, so
 *      there is no default left for a future edit to reach for.
 *   3. `guardInvestmentWrite` is the last gate before these fields enter a
 *      write, for the case a future change re-opens one of the paths above.
 *
 * Pure, so every failure path is testable without a Firestore.
 *
 * Non-goals (ARC-61): nothing here touches the allocation maths, the growth
 * chart, the category editor, or the wallet→category assignment map (which
 * lives in its own settings document and was never part of this field group).
 */

import { stableStringify } from "../expenses/expenseWriteGuard";

export { stableStringify };

export const INVESTMENT_LOAD_ERROR =
  "Could not load your investment data. Editing is disabled so nothing overwrites what is saved — please refresh the page.";

export const INVESTMENT_OVERWRITE_BLOCKED =
  "Your saved investments were protected: placeholder entries were about to replace them. Nothing was overwritten.";

/** The category list a member starts with. A display config, not member data. */
export const DEFAULT_INVESTMENT_CATEGORIES = [
  "Emergency Fund",
  "Retirement",
  "Growth",
  "Income",
  "Speculative",
  "Real Estate",
  "Crypto",
];

/* The names of the demo holdings that used to be seeded into the buffer. Kept
   as NAMES ONLY, deliberately: this module must not contain a seedable
   scaffold, or removing it from getDefaultData would have achieved nothing.
   They exist here to be RECOGNISED and refused, never to be produced. */
export const PLACEHOLDER_INVESTMENT_NAMES = [
  "Emergency Fund",
  "Crypto Wallet 1 Main",
  "401k Wallet",
  "Cosmos wallet",
  "RON Wallet",
  "Company Wallet",
  "SOL Wallet",
  "Home",
  "Tokenized RE",
];

/* The demo monthly ladder, same reasoning: values to be recognised, never
   produced. Order matches Jan..Dec. */
export const PLACEHOLDER_HISTORY_MONTHS = {
  Jan: 250000, Feb: 260000, Mar: 270000, Apr: 285000,
  May: 295000, Jun: 310000, Jul: 315000, Aug: 300000,
  Sep: 320000, Oct: 325000, Nov: 330000, Dec: 325000,
};

const isPlainObject = (v) =>
  typeof v === "object" && v !== null && !Array.isArray(v);

/* Absent means "the member has saved nothing here yet" — a normal, safe state.
   Anything else we cannot read is NOT the same thing and must not be treated
   as one. */
const isAbsent = (v) => v === undefined || v === null;

const isFiniteNumber = (v) => typeof v === "number" && Number.isFinite(v);

/**
 * Is a stored `investments` value one we cannot read?
 *
 * A non-array, an array holding a non-record, or a record whose `name` or
 * `amount` is PRESENT but of the wrong type. The tab reads `inv.name.includes()`
 * and `inv.amount.toLocaleString()` unguarded and the dashboard sums `i.amount`,
 * so a wrong-typed field is both a crash and — once coerced away — a silent
 * corruption of the figure we then write back.
 *
 * Absent `name` / `amount` is NOT unparseable: that is a legacy record shape,
 * handled by `normalizeInvestment` below.
 */
export const isUnparseableInvestments = (v) => {
  if (isAbsent(v)) return false;
  if (!Array.isArray(v)) return true;
  return v.some((entry) => {
    if (!isPlainObject(entry)) return true;
    if (!isAbsent(entry.name) && typeof entry.name !== "string") return true;
    if (!isAbsent(entry.type) && typeof entry.type !== "string") return true;
    if (!isAbsent(entry.amount) && !isFiniteNumber(entry.amount)) return true;
    return false;
  });
};

/**
 * Is a stored `investmentHistory` value one we cannot read?
 *
 * Shape is `{ [year]: { [monthAbbrev]: number } }`. Every writer of this field
 * has always stored numbers (`parseFloat(value) || 0` from the monthly editor,
 * `totalInvestments` from the sync effect), so a non-number month is not a
 * legacy shape — it is a document we are misreading.
 */
export const isUnparseableInvestmentHistory = (v) => {
  if (isAbsent(v)) return false;
  if (!isPlainObject(v)) return true;
  return Object.values(v).some((year) => {
    if (!isPlainObject(year)) return true;
    return Object.values(year).some((amount) => !isFiniteNumber(amount));
  });
};

/**
 * Is a stored `investmentCategories` value one we cannot read?
 *
 * Categories are member-editable (the tab's category editor writes them), so a
 * value we cannot parse is member data we must not replace with the defaults.
 */
export const isUnparseableInvestmentCategories = (v) => {
  if (isAbsent(v)) return false;
  if (!Array.isArray(v)) return true;
  return v.some((c) => typeof c !== "string");
};

/**
 * Bring one stored holding up to the shape the tab renders.
 *
 * Only ABSENT fields are filled in — a present field of the wrong type never
 * reaches here, because `isUnparseableInvestments` already failed the read
 * closed. Coercing those instead would write our guess over the member's value.
 */
export const normalizeInvestment = (inv) => ({
  ...inv,
  name: typeof inv.name === "string" ? inv.name : "",
  type: typeof inv.type === "string" ? inv.type : "Uncategorized",
  amount: isFiniteNumber(inv.amount) ? inv.amount : 0,
});

/**
 * Is this array the demo scaffold rather than member data?
 *
 * Name-based, and it requires MOST of the scaffold to be present. Several of
 * these names ("Emergency Fund", "Home") are ones a member would plausibly
 * choose, so one or two matches is not the incident — the full nine is.
 * Callers pair this with a check that the prior is not itself
 * placeholder-named, so a member who genuinely uses these names keeps their
 * edits.
 */
export const isPlaceholderInvestmentSet = (v) => {
  if (!Array.isArray(v) || v.length === 0) return false;
  const names = v.map((i) => (isPlainObject(i) ? i.name : null));
  if (!names.every((n) => PLACEHOLDER_INVESTMENT_NAMES.includes(n))) return false;
  return new Set(names).size >= 5;
};

/**
 * Is this history the demo ladder rather than member data?
 *
 * Matched loosely on purpose: the tab's `syncCurrentTotal` effect overwrites the
 * CURRENT month with the live portfolio total the moment the scaffold loads, so
 * the ladder as it reaches a write is never all twelve original values. Nine of
 * twelve matching exact demo figures is not a coincidence a member produces.
 */
export const isPlaceholderInvestmentHistory = (v) => {
  if (!isPlainObject(v)) return false;
  return Object.values(v).some((year) => {
    if (!isPlainObject(year)) return false;
    const matches = Object.entries(PLACEHOLDER_HISTORY_MONTHS).filter(
      ([month, amount]) => year[month] === amount
    );
    return matches.length >= 9;
  });
};

/**
 * Has the member changed the investment buffer since the last server-confirmed
 * state? No committed snapshot means nothing has been confirmed yet — treat
 * that as "not dirty" so we never warn about edits the member did not make.
 */
export const hasUnsavedInvestmentChanges = (current, committed) => {
  if (!committed) return false;
  const shape = (s) => ({
    investments: Array.isArray(s?.investments) ? s.investments : [],
    investmentHistory: isPlainObject(s?.investmentHistory)
      ? s.investmentHistory
      : {},
    investmentCategories: Array.isArray(s?.investmentCategories)
      ? s.investmentCategories
      : [],
  });
  return stableStringify(shape(current)) !== stableStringify(shape(committed));
};

/**
 * Decide the investment state a load result is allowed to produce.
 *
 * @param {{ok: boolean, data?: object|null}} result
 *   `ok` is false when the read threw or the service reported failure.
 *   `data` is the loaded document, or null/empty for a brand-new account.
 *
 * @returns {{investments: object[], investmentHistory: object,
 *   investmentCategories: string[],
 *   committed: {investments: object[], investmentHistory: object,
 *     investmentCategories: string[]}|null,
 *   canSave: boolean, error: string}}
 *   `committed` is the server-confirmed snapshot the tab's charts, category
 *   summary and totals render from; null means nothing is confirmed.
 *   `canSave: false` means the tab must be read-only — a failed read tells us
 *   nothing about what the server holds, so writing anything on top of it is
 *   guesswork.
 *
 * Note what this function cannot do: there is no branch that returns a
 * placeholder holding or a placeholder month.
 */
export const resolveInvestmentLoad = ({ ok, data } = {}) => {
  /* One closed state, whatever the reason we do not trust the read. The
     categories still come back as the defaults because they are a display
     config the read-only tab has to render SOMETHING with; `canSave: false`
     is what stops them being written. */
  const failedRead = () => ({
    investments: [],
    investmentHistory: {},
    investmentCategories: [...DEFAULT_INVESTMENT_CATEGORIES],
    committed: null,
    canSave: false,
    error: INVESTMENT_LOAD_ERROR,
  });

  if (!ok) return failedRead();

  /* A present-but-unreadable value is the quiet version of the same bug:
     coercing it away with saving left ENABLED means autosave writes an empty
     portfolio over a server value we merely failed to parse, ~2s after load and
     with no user action. Ignoring a value we cannot read is fine; replacing it
     is the exact data loss this module exists to prevent. Any one of the three
     fields being unreadable closes all three: they are written by one call, and
     a document we are misreading in one place is not one we can vouch for in
     another. */
  if (
    isUnparseableInvestments(data?.investments) ||
    isUnparseableInvestmentHistory(data?.investmentHistory) ||
    isUnparseableInvestmentCategories(data?.investmentCategories)
  ) {
    return failedRead();
  }

  /* A document with no `investments` is either a brand-new account or one saved
     before the field existed. Both mean "the member has entered no holdings",
     and an empty list is what that looks like — not nine worked examples we
     then save on their behalf. */
  const investments = (
    Array.isArray(data?.investments) ? data.investments : []
  ).map(normalizeInvestment);

  const investmentHistory = isPlainObject(data?.investmentHistory)
    ? data.investmentHistory
    : {};

  /* Absent categories are the one safe default in this group: the list is a set
     of labels to pick from, identical for every new account, and never a
     figure. An empty stored array is respected as a deliberate choice. */
  const investmentCategories = Array.isArray(data?.investmentCategories)
    ? data.investmentCategories
    : [...DEFAULT_INVESTMENT_CATEGORIES];

  return {
    investments,
    investmentHistory,
    investmentCategories,
    committed: { investments, investmentHistory, investmentCategories },
    canSave: true,
    error: "",
  };
};

/**
 * Last gate before the investment fields go into a write.
 *
 * @param {{outgoing: object[]|undefined, outgoingHistory: object|undefined,
 *   outgoingCategories: string[]|undefined,
 *   committed: {investments: object[], investmentHistory: object,
 *     investmentCategories: string[]}|null,
 *   server: object|undefined, outgoingTrusted: boolean}} params
 *   `server` is the document as read immediately before the write. `committed`
 *   is our own last server-confirmed snapshot. `outgoingTrusted` is false when
 *   the load that should have produced `outgoing` failed or could not be parsed.
 *
 * @returns {{investments: object[], investmentHistory: object,
 *   investmentCategories: string[], blocked: boolean, warning: string}}
 *
 * The values are substituted rather than the whole save being refused. One save
 * carries every tab's state in a single document write, so refusing outright
 * would strand a member editing their expenses because of a tab they never
 * opened. Substituting keeps the write moving and still guarantees the arrays on
 * the server are never the ones replaced.
 *
 * Deliberately NOT guarded against: an empty `outgoing` replacing a non-empty
 * prior. Selling every holding is a real thing members do — `removeInvestment`
 * already confirms it — and a guard that second-guessed it would make recording
 * that impossible.
 */
export const guardInvestmentWrite = ({
  outgoing,
  outgoingHistory,
  outgoingCategories,
  committed,
  server,
  outgoingTrusted = true,
} = {}) => {
  const asList = (v) => (Array.isArray(v) ? v : []);
  const asMap = (v) => (isPlainObject(v) ? v : {});

  /* We could not read these fields, so `outgoing` is not the member's
     portfolio — it is whatever the UI holds in their absence. Write the
     server's own values straight back, which makes them a no-op for this save.
     Verbatim, including values we failed to parse: we do not know they are safe
     to lose. */
  if (!outgoingTrusted) {
    return {
      investments: asList(server?.investments),
      investmentHistory: asMap(server?.investmentHistory),
      investmentCategories: asList(server?.investmentCategories),
      blocked: false,
      warning: "",
    };
  }

  let blocked = false;
  let investments = asList(outgoing);
  let investmentHistory = asMap(outgoingHistory);
  const investmentCategories = asList(outgoingCategories);

  /* Both copies are consulted. `server` is authoritative and catches a stale
     snapshot; `committed` covers the write where the pre-write read gave us
     nothing to compare against. A prior that is ITSELF the scaffold means the
     member owns these names — leave them their edits. */
  if (isPlaceholderInvestmentSet(outgoing)) {
    const prior = [server?.investments, committed?.investments].find(
      (c) => Array.isArray(c) && c.length > 0 && !isPlaceholderInvestmentSet(c)
    );
    if (prior) {
      investments = prior;
      blocked = true;
    }
  }

  if (isPlaceholderInvestmentHistory(outgoingHistory)) {
    const priorHistory = [
      server?.investmentHistory,
      committed?.investmentHistory,
    ].find(
      (c) =>
        isPlainObject(c) &&
        Object.keys(c).length > 0 &&
        !isPlaceholderInvestmentHistory(c)
    );
    if (priorHistory) {
      investmentHistory = priorHistory;
      blocked = true;
    }
  }

  return {
    investments,
    investmentHistory,
    investmentCategories,
    blocked,
    warning: blocked ? INVESTMENT_OVERWRITE_BLOCKED : "",
  };
};

export default resolveInvestmentLoad;
